import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
// SpaceServicePage.tsx — Co Marca Admin — estilo CategoryPage
import Loading from '../../../components/Loading';
import React, { useEffect, useState } from 'react';
import { Edit, Trash2, Plus, History, Store, ChevronRight, Home, Package, Layers } from 'lucide-react';
import { fetchEspacioServicios, EspacioServicio } from '../../../services/espacioServiciosService';
import { fetchEspacios, fetchServicios, CatalogoItem } from '../../../services/catalogosService';
import AddSpaceServiceModal from '../../../components/SpaceService/AddSpaceServiceModal';
import EditSpaceServiceModal from '../../../components/SpaceService/EditSpaceServiceModal';
import DeleteSpaceServiceModal from '../../../components/SpaceService/DeleteSpaceServiceModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `

.ssp-page { font-family:'DM Sans',sans-serif; }
@keyframes sspFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.ssp-fadeup   { animation:sspFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.ssp-fadeup-1 { animation:sspFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }
.ssp-btn-primary {
    display:inline-flex;align-items:center;gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:9px 20px;font-size:.83rem;font-weight:800;
    cursor:pointer;font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);white-space:nowrap;
}
.ssp-btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.32);}
.ssp-btn-secondary {
    display:inline-flex;align-items:center;gap:7px;
    background:var(--ad-surface, #fff);border:1.5px solid var(--ad-line, #e2e8f0);color:var(--ad-ink, #374151);
    border-radius:11px;padding:9px 14px;font-size:.83rem;font-weight:700;
    cursor:pointer;font-family:'DM Sans',sans-serif;transition:all .2s;
}
.ssp-btn-secondary:hover{background:var(--ad-soft, #f8fafc);border-color:color-mix(in srgb, ${colors.secondary} 25.1%, transparent);color:var(--ad-link, #2854c5);}
.ssp-table-row{transition:background .15s;}
.ssp-table-row:hover{background:var(--ad-soft, #fafcff);}
.ssp-action-btn{width:30px;height:30px;border-radius:9px;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;}
.custom-scroll::-webkit-scrollbar{height:6px;width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background-color:rgba(97, 37, 123,0.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background-color:rgba(97, 37, 123,0.5);}
`;

const SpaceServicePage: React.FC = () => {
    const [servicios, setServicios]         = useState<EspacioServicio[]>([]);
    const [espaciosList, setEspaciosList]   = useState<CatalogoItem[]>([]);
    const [serviciosListCat, setServiciosListCat] = useState<CatalogoItem[]>([]);
    const [showAddModal, setShowAddModal]   = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedServicio, setSelectedServicio] = useState<EspacioServicio|null>(null);
    const [currentPage, setCurrentPage]     = useState(1);
    const itemsPerPage = 10;
    const navigate = useNavigate();
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('espacioservicio', userLevelId);

    const loadServicios = () => {
        fetchEspacioServicios().then(data=>setServicios(data.sort((a,b)=>a.id-b.id))).catch(()=>{});
    };
    const loadCatalogos = () => {
        fetchEspacios().then(setEspaciosList).catch(()=>setEspaciosList([]));
        fetchServicios().then(setServiciosListCat).catch(()=>setServiciosListCat([]));
    };

    useEffect(() => {
        if (perm?.can_read) { loadServicios(); loadCatalogos(); }
        else { setServicios([]); setEspaciosList([]); setServiciosListCat([]); }
    }, [perm?.can_read]);

    const totalPages        = Math.ceil(servicios.length / itemsPerPage);
    const displayedServicios = servicios.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);
    const getNombre = (list: CatalogoItem[], id: number) => list.find(i=>i.id===id)?.nombre || id.toString();

    if (perm === null) return <Loading text="Cargando servicios de espacios..." />;

    return <><LumaResourceWorkspace title="Espacios de Servicios" description="Asignaciones de espacios y servicios" group="Servicios" rows={servicios} columns={[{key:'espacio_nombre',label:'Espacio',render:r=>r.espacio_nombre||espaciosList.find(e=>e.id===r.espacio)?.nombre||'Sin espacio'},{key:'servicio_nombre',label:'Servicio',render:r=>r.servicio_nombre||serviciosListCat.find(e=>e.id===r.servicio)?.nombre||'Sin servicio'},{key:'estado',label:'Estado'}]} idKey="id" nameKey="espacio_nombre" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setShowAddModal(true)}:undefined} onEdit={perm?.can_update?r=>{setSelectedServicio(r);setShowEditModal(true);}:undefined} onDelete={perm?.can_delete?r=>{setSelectedServicio(r);setShowDeleteModal(true);}:undefined} history="/spaces-services/history"/><AddSpaceServiceModal isOpen={showAddModal} onClose={()=>setShowAddModal(false)} onSuccess={()=>{ loadServicios(); loadCatalogos(); setShowAddModal(false); }}/><EditSpaceServiceModal isOpen={showEditModal} onClose={()=>{ setShowEditModal(false); setSelectedServicio(null); }} onSuccess={()=>{ loadServicios(); setShowEditModal(false); }} servicio={selectedServicio}/><DeleteSpaceServiceModal isOpen={showDeleteModal} onClose={()=>{ setShowDeleteModal(false); setSelectedServicio(null); }} onSuccess={()=>{ loadServicios(); setShowDeleteModal(false); }} servicio={selectedServicio}/><ExpiredSessionModal isOpen={isSessionExpired} onClose={async()=>{ setIsSessionExpired(false); await logout(); }}/></>;
};

export default SpaceServicePage;
